// Operations Report — Fase 15 Final Report
import { enterpriseCertificationReport } from '@/core/certification/enterpriseCertificationReport'
import { runDeploymentValidation }        from '@/core/deployment/deploymentValidator'
import { validateRollbackReadiness }      from '@/core/deployment/rollbackValidator'
import { buildBackupStatusReport }        from '@/core/backup/backupOrchestrator'
import { buildIncidentSummary }           from '@/core/incidents/incidentRegistry'
import { getSafetySnapshot }             from '@/core/safety/operationalSafetyLayer'
import { getOrchestratorHealth }         from '@/core/orchestration/runtimeOrchestrator'
import { validateEnvironmentConsistency } from '@/core/deployment/environmentValidator'
import { validateMigrationSafety }        from '@/core/deployment/migrationSafetyValidator'
import { buildAuditSummary }             from '@/core/audit/operationalAuditTrail'
import { getSafeguardSnapshot }          from '@/core/safeguards/productionSafeguards'

export type OperationalCertification = 'NOT READY' | 'DEVELOPMENT READY' | 'STAGING READY' | 'PRODUCTION READY' | 'MISSION CRITICAL READY'

export interface OperationsReport {
  timestamp: string; environment: string
  readinessScore: number; deploySafety: number; rollbackScore: number; backupScore: number
  certification: OperationalCertification; certificationLevel: string
  risks: string[]; recommendations: string[]
  sections: {
    deployment:  { score: number; passed: boolean; blockers: string[] }
    rollback:    { score: number; ready: boolean; blockers: string[] }
    backup:      { restorable: boolean; stale: boolean; hoursSince: number | null }
    incidents:   { open: number; critical: number }
    safety:      { safeMode: boolean; degraded: boolean; killSwitches: number }
    environment: { consistent: boolean; issues: string[] }
    audit:       { criticalCount: number; last24h: number }
    safeguards:  { emergencyStop: boolean; violationCount: number }
  }
  enterprise: { level: string; score: number; hasFails: boolean }
}

export function generateOperationsReport(companyId?: string): OperationsReport {
  const deploy     = runDeploymentValidation(companyId)
  const rollback   = validateRollbackReadiness()
  const backup     = buildBackupStatusReport()
  const incidents  = buildIncidentSummary()
  const safety     = getSafetySnapshot()
  const orch       = getOrchestratorHealth()
  const envReport  = validateEnvironmentConsistency()
  const migration  = validateMigrationSafety()
  const audit      = buildAuditSummary()
  const safeguards = getSafeguardSnapshot()
  const enterprise = enterpriseCertificationReport()

  const risks: string[] = []; const recommendations: string[] = []
  if (!deploy.passed)           risks.push('Deploy blocked: ' + deploy.blockers.join('; '))
  if (!rollback.ready)          risks.push('Rollback not ready: ' + rollback.blockers.join('; '))
  if (backup.isStale)           risks.push('Backup stale: ' + backup.hoursSinceBackup + 'h')
  if (!backup.restorable)       risks.push('No restorable backup')
  if (incidents.critical > 0)   risks.push(incidents.critical + ' critical incident(s) open')
  if (safety.safeMode)          risks.push('System in safe mode')
  if (safety.degraded.active)   risks.push('System degraded: ' + (safety.degraded.reason ?? 'unknown'))
  if (orch.failed > 0)          risks.push(orch.failed + ' engine(s) failed')
  if (!envReport.consistent)    risks.push('Environment inconsistent: ' + envReport.issues.join('; '))
  if (!migration.safeToMigrate) risks.push('Migration safety check failed')
  if (safeguards.emergencyStop) risks.push('Emergency stop active')
  if (safeguards.violations.length > 5) risks.push(safeguards.violations.length + ' safeguard violations')
  if (audit.criticalCount > 0)  risks.push(audit.criticalCount + ' critical audit entries')

  recommendations.push(...deploy.warnings.slice(0, 3))
  recommendations.push(...backup.recommendations.slice(0, 2))
  if (incidents.open > 0) recommendations.push('Resolve ' + incidents.open + ' open incident(s) before production')
  if (migration.overallRisk === 'high' || migration.overallRisk === 'critical')
    recommendations.push(...migration.recommendations.slice(0, 2))

  const deploySafety  = deploy.readinessScore
  const rollbackScore = rollback.score
  const backupScore   = backup.restorable ? (backup.isStale ? 50 : 100) : 0
  const safetyScore   = (safety.safeMode || safety.degraded) ? 40 : orch.failed > 0 ? 70 : 100
  const envScore      = envReport.consistent ? 100 : 60
  const incScore      = incidents.critical > 0 ? 30 : incidents.open > 0 ? 70 : 100
  const readinessScore = Math.round(deploySafety*0.25 + rollbackScore*0.20 + backupScore*0.15 + safetyScore*0.20 + envScore*0.10 + incScore*0.10)

  let certification: OperationalCertification = 'NOT READY'
  if      (readinessScore >= 90 && risks.length === 0)       certification = 'MISSION CRITICAL READY'
  else if (readinessScore >= 80 && risks.length <= 2)        certification = 'PRODUCTION READY'
  else if (readinessScore >= 65 && risks.length <= 4)        certification = 'STAGING READY'
  else if (readinessScore >= 40)                              certification = 'DEVELOPMENT READY'


  return {
    timestamp: new Date().toISOString(), environment: envReport.environment,
    readinessScore, deploySafety, rollbackScore, backupScore,
    certification, certificationLevel: enterprise.certificationLevel,
    risks, recommendations,
    sections: {
      deployment:  { score: deploySafety,   passed:     deploy.passed,    blockers:   deploy.blockers },
      rollback:    { score: rollbackScore,  ready:      rollback.ready,   blockers:   rollback.blockers },
      backup:      { restorable: backup.restorable, stale: backup.isStale, hoursSince: backup.hoursSinceBackup },
      incidents:   { open: incidents.open,  critical:   incidents.critical },
      safety:      { safeMode: safety.safeMode, degraded: safety.degraded.active, killSwitches: safety.killSwitches.length },
      environment: { consistent: envReport.consistent, issues: envReport.issues },
      audit:       { criticalCount: audit.criticalCount, last24h: audit.last24hCount },
      safeguards:  { emergencyStop: safeguards.emergencyStop, violationCount: safeguards.violations.length },
    },
    enterprise: { level: enterprise.certificationLevel, score: enterprise.overallScore, hasFails: enterprise.dimensions.some((d) => d.level === 'fail') },
  }
}

export function printOperationsReport(r: OperationsReport): void {
  console.group('[ops] ' + r.certification + ' — ' + r.readinessScore + '%')
  console.log('[ops] Deploy: ' + r.deploySafety + '% | Rollback: ' + r.rollbackScore + '% | Backup: ' + r.backupScore + '%')
  console.log('[ops] Enterprise: ' + r.enterprise.level + ' (' + r.enterprise.score + '/100)')
  if (r.risks.length)           { console.group('[ops] RISKS');           r.risks.forEach(x => console.warn(' •', x));    console.groupEnd() }
  if (r.recommendations.length) { console.group('[ops] RECOMMENDATIONS'); r.recommendations.forEach(x => console.log(' ->', x)); console.groupEnd() }
  console.groupEnd()
}
